/** DK108 第一行末端旋钮：按下 / 顺时针 / 逆时针（HFD SDK wheelKeys 独立槽位） */

export type HfdDk108KnobActionId = "press" | "clockwise" | "counterClockwise";

export type HfdDk108KnobActionSpec = {
  id: HfdDk108KnobActionId;
  /** UI 展示名 */
  label: string;
  /** SDK keyIndex（0~127），写入 setKeyData 第二参数 wheelKeys */
  keyIndex: number;
};

/**
 * 槽位依据实机 getKeyData + @hfd-sdk basicChars 校准：
 * - 15：MUTE / 按下（pageType=3 param=226）
 * - 13 / 14：VOLINC / VOLDEC，对应顺时针 / 逆时针（param=233 / 234）
 */
export const HFD_DK108_KNOB_ACTIONS: readonly HfdDk108KnobActionSpec[] = [
  { id: "press", label: "按下", keyIndex: 15 },
  { id: "clockwise", label: "顺时针旋转", keyIndex: 13 },
  { id: "counterClockwise", label: "逆时针旋转", keyIndex: 14 },
] as const;

/** wheelKeys 须一并下发的旋钮槽位 */
export const HFD_DK108_KNOB_WHEEL_SLOT_INDICES: readonly number[] =
  HFD_DK108_KNOB_ACTIONS.map((a) => a.keyIndex);

/** DK108 旋钮出厂默认展示用 xsKeycode（MUTE / VOL+ / VOL-） */
export const HFD_DK108_KNOB_FACTORY_DISPLAY_KEYCODES: Readonly<
  Record<HfdDk108KnobActionId, number>
> = {
  press: 8418,
  clockwise: 8425,
  counterClockwise: 8426,
};

/** DK108 旋钮出厂默认（与实机 getKeyData 一致：MUTE / VOL+ / VOL-） */
export const HFD_DK108_KNOB_FACTORY_SLOTS: readonly Readonly<{
  keyIndex: number;
  pageType: number;
  param1: number;
  param2: number;
  param3: number;
}>[] = [
  { keyIndex: 15, pageType: 3, param1: 226, param2: 0, param3: 0 },
  { keyIndex: 13, pageType: 3, param1: 233, param2: 0, param3: 0 },
  { keyIndex: 14, pageType: 3, param1: 234, param2: 0, param3: 0 },
];

export type HfdDk108KnobKeyDataSlot = {
  pageType?: number;
  param1?: number;
  param2?: number;
  param3?: number;
};

/** 将旋钮三向槽位写回出厂默认（恢复默认改键时调用） */
export function applyHfdDk108KnobFactoryDefaults(
  keyData: HfdDk108KnobKeyDataSlot[]
): void {
  if (!Array.isArray(keyData)) return;
  for (const slot of HFD_DK108_KNOB_FACTORY_SLOTS) {
    if (slot.keyIndex < 0 || slot.keyIndex >= keyData.length) continue;
    keyData[slot.keyIndex] = {
      pageType: slot.pageType,
      param1: slot.param1,
      param2: slot.param2,
      param3: slot.param3,
    };
  }
}

/** 矩阵 row0 col20：旋钮 UI 占位（xsKeycode 62208，仅展示不参与普通改键槽位解析） */
export const HFD_DK108_KNOB_MATRIX_ROW = 0;
export const HFD_DK108_KNOB_MATRIX_COL = 20;
export const HFD_DK108_KNOB_MATRIX_KEYCODE = 62208;

/** DK108、X75GC 等华丰达 wheelKeys 旋钮改键（槽位与 SDK 协议同 DK108） */
export function isHfdDk108KnobKeyboard(keyboardType?: string | null): boolean {
  const k = String(keyboardType ?? "").trim();
  return k === "DK108" || k === "x75gc";
}

export function findHfdDk108KnobActionByKeyIndex(
  keyIndex: number
): HfdDk108KnobActionSpec | undefined {
  return HFD_DK108_KNOB_ACTIONS.find((a) => a.keyIndex === keyIndex);
}

/** 设备未改键或槽位为空时，回退到出厂默认 xsKeycode */
export function resolveDk108KnobDisplayKeycode(
  actionId: HfdDk108KnobActionId,
  deviceKeycode: number
): number {
  const code = Number(deviceKeycode);
  if (Number.isFinite(code) && code > 0) return code;
  return HFD_DK108_KNOB_FACTORY_DISPLAY_KEYCODES[actionId] ?? 0;
}

export function resolveDk108KnobBindingsRecord(
  partial: Partial<Record<HfdDk108KnobActionId, number>> = {}
): Record<HfdDk108KnobActionId, number> {
  return {
    press: resolveDk108KnobDisplayKeycode("press", partial.press ?? 0),
    clockwise: resolveDk108KnobDisplayKeycode(
      "clockwise",
      partial.clockwise ?? 0
    ),
    counterClockwise: resolveDk108KnobDisplayKeycode(
      "counterClockwise",
      partial.counterClockwise ?? 0
    ),
  };
}
